/**
 * ProductFilterModal Component
 * 
 * Princípios aplicados:
 * - Componentização: Usa Input, Select, Button e Modal do design system
 * - Single Responsibility: Modal de filtros para produtos
 */
import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

interface ProductFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (name: string, operator: 'equals' | 'contains', minValue?: number, maxValue?: number) => void;
    initialName?: string;
    initialOperator?: 'equals' | 'contains';
    initialMinValue?: number;
    initialMaxValue?: number;
}

const OPERATOR_OPTIONS = [
    { value: 'equals', label: 'Igual a' },
    { value: 'contains', label: 'Contém' },
];

const ProductFilterModal: React.FC<ProductFilterModalProps> = ({
    isOpen,
    onClose,
    onApply,
    initialName = '',
    initialOperator = 'equals',
    initialMinValue,
    initialMaxValue
}) => {
    const [name, setName] = useState(initialName);
    const [operator, setOperator] = useState<'equals' | 'contains'>(initialOperator);
    const [minValue, setMinValue] = useState<string>(initialMinValue?.toString() || '');
    const [maxValue, setMaxValue] = useState<string>(initialMaxValue?.toString() || '');

    useEffect(() => {
        if (isOpen) {
            setName(initialName);
            setOperator(initialOperator);
            setMinValue(initialMinValue?.toString() || '');
            setMaxValue(initialMaxValue?.toString() || '');
        }
    }, [isOpen, initialName, initialOperator, initialMinValue, initialMaxValue]);

    const handleApply = () => {
        const min = minValue ? parseFloat(minValue) : undefined;
        const max = maxValue ? parseFloat(maxValue) : undefined;
        onApply(name, operator, min, max);
        onClose();
    };

    const handleClear = () => {
        setName('');
        setOperator('equals');
        setMinValue('');
        setMaxValue('');
        onApply('', 'equals', undefined, undefined);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header onClose={onClose}>
                Filtrar produtos
            </Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    {/* Campo Nome */}
                    <div>
                        <label className="block text-sm font-medium text-default mb-2">
                            Nome do Produto
                        </label>
                        <div className="grid grid-cols-[140px_1fr] gap-3">
                            <Select
                                value={operator}
                                onChange={(e) => setOperator(e.target.value as 'equals' | 'contains')}
                                options={OPERATOR_OPTIONS}
                                selectSize="sm"
                            />
                            <Input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Digite o nome..."
                                inputSize="sm"
                            />
                        </div>
                    </div>

                    {/* Faixa de Valor */}
                    <div>
                        <label className="block text-sm font-medium text-default mb-2">
                            Faixa de Valor (R$)
                        </label>
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
                            <Input
                                type="number"
                                value={minValue}
                                onChange={(e) => setMinValue(e.target.value)}
                                placeholder="Valor mínimo"
                                step="0.01"
                                min="0"
                                inputSize="sm"
                            />
                            <span className="text-muted text-sm">até</span>
                            <Input
                                type="number"
                                value={maxValue}
                                onChange={(e) => setMaxValue(e.target.value)}
                                placeholder="Valor máximo"
                                step="0.01"
                                min="0"
                                inputSize="sm"
                            />
                        </div>
                    </div>

                    {/* Informação adicional */}
                    <div className="text-xs text-muted bg-surface-subtle p-3 rounded-lg space-y-1">
                        <p>Use "Igual a" para buscar nomes exatos ou "Contém" para buscar parte do nome.</p>
                        <p>Defina uma faixa de valor para filtrar produtos por preço.</p>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={handleClear} variant="ghost" size="sm">
                    Limpar filtros
                </Button>
                <Button onClick={onClose} variant="secondary" size="sm">
                    Cancelar
                </Button>
                <Button onClick={handleApply} variant="default" size="sm">
                    Aplicar filtros
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProductFilterModal;

